import { createPortal } from 'react-dom';
import { Tabs, Tab } from '@mui/material';

import JxFormResizableBase from '../../../JxControls/JxForms/JxFormResizableBase.js';
import JxDialogState from '../../../JxControls/JxForms/JxDialogState.js';
import jxWindowEngine from '../../../JxControls/JxWindowManager/JxWindowEngine.js';
import jxWindowManager from '../../../JxControls/JxWindowManager/JxWindowManager.jsx';
import JxTitleBar from '../../../JxControls/JxTitleBar/JxTitleBar.jsx';
import cardPainterManager from '../../../Widgets/DataPainterWidget/Core/CardPainterManager.jsx';
import '../../../JxControls/JxTheme.css';

import { TITLE_BAR_ACTIONS } from './UmlSchemaDlgTitleBarActions.jsx';
import { createSchema } from './UmlSchemaDlgSchema.js';
import UmlSchemaDlgBody from './UmlSchemaDlgBody.jsx';
import UmlSchemaStartTab from './UmlSchemaStartTab.jsx';
import UmlSchemaImportTab from './UmlSchemaImportTab.jsx';
import UmlSchemaFileImportTab, { DIAGRAM_MODES } from './UmlSchemaFileImportTab.jsx';
import UmlSchemaBodyTab from './UmlSchemaBodyTab.jsx';
import LoadUmlNoteDlg from '../LoadUmlSchemaDlg/LoadUmlNoteDlg.jsx';
import umlSchemaFormData from '../../Core/UmlSchemaFormData.js';
import { saveCurrentSchema } from '../../Core/UmlCanvasSaveAction.js';
import umlNotesEngine from '../../Core/UmlNotesEngine.js';
import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';

import { DEFAULT_POS, DEFAULT_GEO, TABS, TABS_SX } from './UmlSchemaConfigDlgHelper.js';

import '../../index.jsx'; // side-effect: registreert 'uml:workspace:1' bij painterRegistry — zonder deze import blijft CardPainterHost.resolve('uml:workspace:1') null en rendert 'ie stil niks (zie CardPainterHost.jsx: "Graceful skip")

import './UmlSchemaConfigDlg.css';

// zie UmlSchemaConfigDlg.txt: class UmlSchemaConfigDlg (class JSDoc)
export default class UmlSchemaConfigDlg extends JxFormResizableBase {
    static defaultGeo = DEFAULT_GEO();

    constructor(props) {
        super(props);
        this.name = 'UmlSchemaDlg';
        this.schema = createSchema();
        this.state = {
            ...this.state,
            inputs: this.buildInputsFromEngine(),
            isSaving: false,
            activeTab: TABS.START,
            fileImport: { droppedFiles: [], diagramMode: DIAGRAM_MODES.CLASS },
        };
        this.loadDlg = new JxDialogState(() => this.forceUpdate(), {});
    }

    // Merges umlCanvasEngine.loadedSchemaInfo (rk/name/remarks — the durable identity)
    // onto the form defaults. Needed because this dialog instance is fully destroyed on
    // close (see JxWindowManager.closeWindow), so a reopen must recover rk from the engine
    // rather than starting blank. Deliberately does NOT call loadFromSchema here — shapes
    // already live in umlCanvasEngine and may have been edited since the note was loaded.
    buildInputsFromEngine() {
        const defaults = umlSchemaFormData.createDefault();
        const info = umlCanvasEngine.loadedSchemaInfo;
        if (!info?.rk) return defaults;
        return { ...defaults, rk: info.rk, name: info.name, remarks: info.remarks };
    }

    componentDidMount() {
        super.componentDidMount();
        this.bringToTop();
        // zie UmlSchemaConfigDlg.txt: componentDidMount
        this._unsubscribeCanvas = umlCanvasEngine.subscribe(() => this.forceUpdate());

        // /uml command wants a straight-to-blank-canvas start (registerUmlCommand.js passes
        // autoNew=true) — but only when NOTHING is loaded yet, so reopening the dialog while
        // an existing schema/canvas is already active never silently wipes it.
        if (this.props.autoNew && !umlCanvasEngine.loadedSchemaInfo?.rk) {
            this.handleNew();
        }
    }

    componentWillUnmount() {
        this._unsubscribeCanvas?.();
        super.componentWillUnmount();
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
    }

    getInitialPos() {
        return DEFAULT_POS();
    }

    getInitialSize() {
        const c = jxWindowEngine.getPos(this.getId());
        if (c !== null) {
            return { top: c.posY, left: c.posX, width: c.width, height: c.height };
        }
        return { width: 480, height: 460 };
    }

    computeTitle() {
        const name = this.state.inputs.name?.trim();
        return name ? `Uml Schema: ${name}` : 'Uml Schema';
    }

    getTaskbarTitle() {
        return this.computeTitle();
    }

    // zie UmlSchemaConfigDlg.txt: openCanvasCard
    openCanvasCard() {
        cardPainterManager.openCard('uml:workspace:1', { type: 'uml:workspace', ver: 1, target: 'active' });
    }

    handleClose = async (action) => {
        try {
            this.props.onClose?.();
        } catch (error) {
            this.logError('handleClose', error.message);
        }
    };

    // ── Files tab state (lifted here, see UmlSchemaFileImportTab's JSDoc) ─
    // Behave like useState setters (accept a value OR an updater function)
    // so UmlSchemaFileImportTab's existing `setDroppedFiles(current => ...)`
    // calls work unchanged.
    handleDroppedFilesChange = (updater) => {
        this.setState(function updateFileImport(prevState) {
            const prevFiles = prevState.fileImport.droppedFiles;
            const nextFiles = typeof updater === 'function' ? updater(prevFiles) : updater;
            return { fileImport: { ...prevState.fileImport, droppedFiles: nextFiles } };
        });
    };

    handleDiagramModeChange = (updater) => {
        this.setState(function updateFileImport(prevState) {
            const prevMode = prevState.fileImport.diagramMode;
            const nextMode = typeof updater === 'function' ? updater(prevMode) : updater;
            return { fileImport: { ...prevState.fileImport, diagramMode: nextMode } };
        });
    };

    // ── Tab switch (klik op tab-strip zelf) ───────────────
    handleTabChange = (event, newTab) => {
        this.setState({ activeTab: newTab });
    };

    // ── New ──────────────────────────────────────────────
    handleNew = () => {
        umlCanvasEngine.loadFromSchema({ shapes: [], connectors: [] });
        umlCanvasEngine.setLoadedSchemaInfo({ rk: null, name: '', remarks: '' });
        this.setState({
            inputs: umlSchemaFormData.createDefault(),
            activeTab: TABS.PROPERTIES,
        });
        this.openCanvasCard();
    };

    // ── Load Uml (opent child-dialog met de lijst) ────────
    handleOpenLoadDlg = () => {
        this.loadDlg.openDlg();
    };

    handleLoadSelected = (note) => {
        try {
            if (!note) {
                this.logError('handleLoadSelected', `note not found: ${note}`);
                return;
            }

            const form = umlSchemaFormData.fromNote(note);
            // ZIE class-comment "BEKENDE GAP" — form.shapes/connectors zijn plain objects,
            // renderen nu nog niet correct als Package/Class-widgets.
            umlCanvasEngine.loadFromSchema({ shapes: form.shapes, connectors: form.connectors, viewConfig: form.viewConfig, viewport: form.viewport });
            umlCanvasEngine.setLoadedSchemaInfo({ rk: form.rk, name: form.name, remarks: form.remarks });
            this.setState({
                inputs: { rk: form.rk, name: form.name, service: form.service, format: form.format, remarks: form.remarks },
                activeTab: TABS.PROPERTIES,
            });
            this.openCanvasCard();
        } catch (error) {
            this.logError('handleLoadSelected', error.message);
        }
    };

    // ── Save ─────────────────────────────────────────────
    // Thin wrapper around the single shared saveCurrentSchema (Core/
    // UmlCanvasSaveAction.js) — same implementation as the toolbar Save button
    // and the S-key shortcut, just with this dialog's own local form state
    // (this.state.inputs: name/service/format/remarks, possibly edited but not
    // yet reflected in umlCanvasEngine.loadedSchemaInfo) passed as overrides.
    handleSave = async () => {
        this.setState({ isSaving: true });
        try {
            const result = await saveCurrentSchema(this.state.inputs);
            if (result && result.rk !== this.state.inputs.rk) {
                this.setState({ inputs: { ...this.state.inputs, rk: result.rk } });
            }
            this.onMessage?.('UmlSchema opgeslagen', 'info');
        } catch (error) {
            this.logError('handleSave', error.message);
        } finally {
            this.setState({ isSaving: false });
        }
    };

    renderActiveTab() {
        if (this.state.activeTab === TABS.START) {
            return <UmlSchemaStartTab onNew={this.handleNew} onOpenLoadDlg={this.handleOpenLoadDlg} />;
        }
        if (this.state.activeTab === TABS.IMPORT) {
            return <UmlSchemaImportTab onSave={this.handleSave} isSaving={this.state.isSaving} name={this.state.inputs.name} />;
        }
        if (this.state.activeTab === TABS.FILES) {
            return (
                <UmlSchemaFileImportTab
                    onSave={this.handleSave}
                    isSaving={this.state.isSaving}
                    name={this.state.inputs.name}
                    droppedFiles={this.state.fileImport.droppedFiles}
                    setDroppedFiles={this.handleDroppedFilesChange}
                    diagramMode={this.state.fileImport.diagramMode}
                    setDiagramMode={this.handleDiagramModeChange}
                />
            );
        }
        if (this.state.activeTab === TABS.BODY) {
            return <UmlSchemaBodyTab onSave={this.handleSave} isSaving={this.state.isSaving} name={this.state.inputs.name} />;
        }
        return (
            <UmlSchemaDlgBody
                form={this.state.inputs}
                onChange={this.handleChange}
                onSave={this.handleSave}
                isSaving={this.state.isSaving}
            />
        );
    }

    render() {
        if (!this.props.open || this.isMinimized) return null;

        return createPortal(
            <>
                <div {...this.panelProps} style={this.panelStyle} className={`jx-panel usd-panel ${this.panelActiveClass}`}>
                    <JxTitleBar
                        id={`${this.name}_titlebar`}
                        title={this.computeTitle()}
                        variant="toolwindow"
                        isDraggable={true}
                        dragHandleProps={this.dragHandleProps}
                        actions={TITLE_BAR_ACTIONS(() => this.handleClose(-1))}
                        isMinimizable={true}
                        windowId={this.getId()}
                    />

                    <Tabs value={this.state.activeTab} onChange={this.handleTabChange} sx={TABS_SX}>
                        <Tab label="Start" value={TABS.START} disableRipple />
                        <Tab label="Properties" value={TABS.PROPERTIES} disableRipple />
                        <Tab label="Import" value={TABS.IMPORT} disableRipple />
                        <Tab label="Files" value={TABS.FILES} disableRipple />
                        <Tab label="Body" value={TABS.BODY} disableRipple />
                    </Tabs>

                    {this.renderActiveTab()}

                    {this.renderResizeHandles()}
                </div>

                <LoadUmlNoteDlg open={this.loadDlg.open} onClose={this.loadDlg.closeDlg} onSelect={this.handleLoadSelected} />
            </>,
            document.body,
        );
    }
}

// zie UmlSchemaConfigDlg.txt: jxWindowManager.registerComponent (bottom of file)
jxWindowManager.registerComponent('UmlSchemaDlg', UmlSchemaConfigDlg);
